/* eslint-disable react/jsx-props-no-spreading */
import {
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as PlusIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import log from 'loglevel';
import { ChangeEventHandler, SyntheticEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import RecruitingTypeSelect from '../../components/RecruitingTypeSelect';
import { useCreateAccount, useGetAssociatedCompanyLinkedin } from '../../hooks/useAccount';
import { generateSubdomain } from '../../utils';
import useNavigate from '../../hooks/useNavigate';
import { AccountRecruitingUsageType } from '../../consts/enums';
import { Logo } from './CreateAccount.styled';
import { FORBIDDEN_SUBDOMAIN_NAMES, LINKED_IN_URL_FALLBACK } from './CreateAccount.consts';

type CreateAccountFormState = {
  linkedInUrl?: string;
  isCompanyWithoutLinkedInUrl: boolean;
  displayName: string;
  subDomainName: string;
  allowedEmailDomain: string;
  recruitingUsageType: AccountRecruitingUsageType | null;
};

export const CreateAccountPage = (): JSX.Element => {
  const createAccount = useCreateAccount();
  const getAssociatedCompany = useGetAssociatedCompanyLinkedin();

  const [searchParams] = useSearchParams();
  const { goToChoosePlan } = useNavigate();

  const userEmail = decodeURIComponent(searchParams.get('userEmail'));

  const [company, setCompany] = useState<Company>(null);
  const [formError, setFormError] = useState<string>(null);

  const formik = useFormik<CreateAccountFormState>({
    initialValues: {
      linkedInUrl: null,
      isCompanyWithoutLinkedInUrl: false,
      displayName: null,
      subDomainName: null,
      allowedEmailDomain: userEmail?.split('@')?.[1] ?? null,
      recruitingUsageType: null,
    },
    validateOnChange: false,
    validationSchema: Yup.object().shape({
      linkedInUrl: Yup.string().nullable(),
      isCompanyWithoutLinkedInUrl: Yup.boolean(),
      displayName: Yup.string().nullable().required('Required Field'),
      subDomainName: Yup.string()
        .nullable()
        .test(
          'not-excluded',
          'This name is not allowed',
          (value) => {
            if (!value) return true; // skip if empty, let "required" handle it

            return FORBIDDEN_SUBDOMAIN_NAMES.every((ex) => ex.toLowerCase() !== value.toLowerCase());
          },
        )
        .matches(/^[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*$/)
        .required('Required Field'),
      allowedEmailDomain: Yup.string()
        .nullable()
        .matches(
          /^[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*\.[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*(?:\.[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*)*$/,
          'Email Domain should be in format: domain.com',
        )
        .required('Required Field'),
      recruitingUsageType: Yup.mixed().oneOf<AccountRecruitingUsageType>(Object.values(AccountRecruitingUsageType)).required('Required Field'),
    }),
    onSubmit: async (values): Promise<void> => {
      const {
        linkedInUrl,
        displayName,
        subDomainName,
        allowedEmailDomain,
        recruitingUsageType,
      } = values;

      try {
        const account = await createAccount.mutateAsync({
          displayName,
          allowedEmailDomain,
          subDomainName,
          logo: company?.logo || '',
          companyInfo: {
            id: company?.id ?? '',
            name: company?.name ?? '',
            city: company?.location?.city ?? '',
            state: company?.location?.state ?? '',
            country: company?.location?.country ?? '',
            size: 'SMALL',
            linkedinUrl: linkedInUrl ?? LINKED_IN_URL_FALLBACK,
          },
          recruitingUsageType,
          accountStatus: 'ACTIVE',
          maximumOpenPositions: 0,
        });

        goToChoosePlan(account.id, userEmail, true);
      } catch (e) {
        const err = e as AxiosError;
        const message = err.response?.data?.message;
        log.error(err);
        setFormError(message);
      }
    },
  });

  const {
    handleSubmit,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    isValid,
    isSubmitting,
  } = formik;

  const handleLinkedInUrlChange: ChangeEventHandler<HTMLInputElement> = async (
    event,
  ) => {
    handleChange(event);
    const newLinkedInUrl = event.target.value;

    if (newLinkedInUrl?.length) {
      try {
        const associatedCompany = await getAssociatedCompany.mutateAsync(
          newLinkedInUrl,
        );

        if (associatedCompany) {
          setCompany(associatedCompany);
          await setFieldValue('displayName', associatedCompany?.name, false);
          await setFieldValue(
            'subDomainName',
            generateSubdomain(associatedCompany?.name),
            false,
          );
        }
      } catch (e) {
        log.error(e);
      }
    }
  };

  const handleIsCompanyWithoutLinkedInUrlChange = async (
    event: SyntheticEvent,
    checked: boolean,
  ) => {
    handleChange(event);

    if (checked) {
      await setFieldValue('linkedInUrl', null, false);
      setCompany(null);
    }
  };

  const getFormProps = (fieldName: keyof CreateAccountFormState) => ({
    name: fieldName,
    error: touched[fieldName] && !!errors[fieldName],
    helperText: touched[fieldName] && errors[fieldName],
    value: values[fieldName],
    onChange: handleChange,
    onBlur: handleBlur,
  });

  return (
    <Container maxWidth="md" sx={ { paddingBlock: '20px' } }>
      <Stack
        direction="row"
        alignItems="center"
        spacing={ 1 }
        sx={ { paddingBlock: '24px' } }
      >
        <PlusIcon fontSize="large" />

        <Typography variant="h3">Create New Account</Typography>
      </Stack>

      <Card sx={ { padding: '24px' } }>
        <form onSubmit={ handleSubmit }>
          <Stack spacing={ 3 }>
            <Grid container spacing={ 2 }>
              <Grid item xs={ 6 }>
                <Stack>
                  <TextField
                    label="LinkedIn URL"
                    { ...getFormProps('linkedInUrl') }
                    onChange={ handleLinkedInUrlChange }
                    disabled={
                      values.isCompanyWithoutLinkedInUrl || isSubmitting
                    }
                    InputLabelProps={ { shrink: true } }
                  />

                  <FormControlLabel
                    control={ <Checkbox /> }
                    label="The company has no LinkedIn account"
                    { ...getFormProps('isCompanyWithoutLinkedInUrl') }
                    onChange={ handleIsCompanyWithoutLinkedInUrlChange }
                    disabled={ isSubmitting }
                  />
                </Stack>
              </Grid>

              <Grid item xs={ 4 }>
                {getAssociatedCompany.isLoading ? (
                  <CircularProgress />
                ) : (
                  company && (
                    <Stack direction="row" spacing={ 2 }>
                      <Logo src={ company?.logo } alt="logo" />

                      <Stack>
                        <Typography variant="h6">
                          <strong>{company?.name}</strong>
                        </Typography>
                        <Typography variant="body2">
                          {company?.description}
                        </Typography>
                      </Stack>
                    </Stack>
                  )
                )}
              </Grid>

              <Grid item xs={ 4 }>
                <TextField
                  label="Account Name"
                  { ...getFormProps('displayName') }
                  fullWidth
                  InputLabelProps={ { shrink: true } }
                  disabled={
                    (!company && !values.isCompanyWithoutLinkedInUrl) ||
                    isSubmitting
                  }
                />
              </Grid>

              <Grid item xs={ 4 }>
                <TextField
                  label="Sub-domain"
                  { ...getFormProps('subDomainName') }
                  fullWidth
                  InputLabelProps={ { shrink: true } }
                  disabled={
                    (!company && !values.isCompanyWithoutLinkedInUrl) ||
                    isSubmitting
                  }
                  InputProps={ {
                    endAdornment: (
                      <InputAdornment position="start">
                        .goperfect.com
                      </InputAdornment>
                    ),
                  } }
                />
              </Grid>

              <Grid item xs={ 4 }>
                <TextField
                  label="Email Domain"
                  { ...getFormProps('allowedEmailDomain') }
                  fullWidth
                  InputLabelProps={ { shrink: true } }
                  disabled={
                    (!company && !values.isCompanyWithoutLinkedInUrl) ||
                    isSubmitting
                  }
                  placeholder="domain.com"
                  InputProps={ {
                    startAdornment: (
                      <InputAdornment position="start">@</InputAdornment>
                    ),
                  } }
                />
              </Grid>

              <Grid item xs={ 4 }>
                <RecruitingTypeSelect
                  value={ values.recruitingUsageType || '' }
                  onChange={ (value) => setFieldValue('recruitingUsageType', value, true) }
                  error={ touched.recruitingUsageType && !!errors.recruitingUsageType }
                  helperText={ touched.recruitingUsageType && errors.recruitingUsageType ? errors.recruitingUsageType as string : '' }
                  disabled={ isSubmitting }
                  required
                />
              </Grid>

            </Grid>

            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={ 2 }
            >
              {formError && <Typography color="error">{formError}</Typography>}

              <Button
                variant="contained"
                type="submit"
                disabled={ !isValid || isSubmitting }
              >
                Submit
              </Button>
            </Stack>
          </Stack>
        </form>
      </Card>
    </Container>
  );
};
